/* ============================================================
   mw-cart-extras.js  (Horizon module)
   Powers three cart-drawer features:
     1. "Maximize your results" — Add buttons on suggested products.
     2. "Extend warranty" — a checkbox that adds/removes a product.
     3. "Complimentary scrunchie pack" — auto-added when the bundle
        is in cart, auto-removed if the bundle leaves.

   Uses Horizon's OWN section renderer to refresh the cart, so the
   drawer, totals, shipping bar, and this file's features all update
   together — no bolt-on, no polling, no class collisions.
   ============================================================ */
import { sectionRenderer } from '@theme/section-renderer';

const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
const ADD_URL = routes.cart_add_url || '/cart/add';
const CHANGE_URL = routes.cart_change_url || '/cart/change';
const CART_URL = routes.cart_url ? `${routes.cart_url}.js` : '/cart.js';

/* ---------- Config: fill these in ---------- */
const MW_BUNDLE_PRODUCT_ID = '16026690355281';    // Product ID of "the bundle"
const MW_SCRUNCHIE_VARIANT_ID = '660469437300817'; // Variant ID of the scrunchie pack gift

/** Collect every cart section id currently on the page (drawer + page). */
function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

/** Re-render the cart section(s) from the server — Horizon's native refresh. */
function refreshCart() {
  const ids = cartSectionIds();
  ids.forEach((id) => {
    try {
      sectionRenderer.renderSection(id, { cache: false });
    } catch (err) {
      console.error('[mw-cart-extras] refresh failed for', id, err);
    }
  });
  mwSyncScrunchieGift();
}

function cartPost(url, payload) {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  }).then((res) => {
    if (!res.ok) throw new Error('Cart request failed: ' + res.status);
    return res.json();
  });
}

function addVariant(id) {
  return cartPost(ADD_URL, { items: [{ id: Number(id), quantity: 1 }] }).then(refreshCart);
}

function removeVariant(id) {
  return cartPost(CHANGE_URL, { id: Number(id), quantity: 0 }).then(refreshCart);
}

/* ---------- 1. Suggested-product Add buttons ---------- */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-mw-add]');
  if (!btn) return;
  event.preventDefault();
  if (btn.disabled) return;

  const label = btn.textContent;
  btn.disabled = true;
  btn.textContent = '…';

  addVariant(btn.getAttribute('data-mw-add'))
    .catch((err) => {
      console.error('[mw-cart-extras] add failed', err);
      btn.disabled = false;
      btn.textContent = label;
    });
  // On success the section re-renders and this button is replaced, so no reset needed.
});

/* ---------- 2. Warranty checkbox ---------- */
document.addEventListener('change', (event) => {
  const box = event.target.closest('[data-mw-warranty]');
  if (!box) return;

  const id = box.getAttribute('data-mw-warranty');
  const wrap = box.closest('.mw-warranty');
  if (wrap) wrap.classList.add('mw-warranty--loading');

  const action = box.checked ? addVariant(id) : removeVariant(id);
  action.catch((err) => {
    console.error('[mw-cart-extras] warranty toggle failed', err);
    box.checked = !box.checked; // revert on failure
    if (wrap) wrap.classList.remove('mw-warranty--loading');
  });
  // On success the section re-renders with the correct checkbox state.
});

/* ---------- 3. Complimentary scrunchie pack auto-add ---------- */
let mwGiftSyncInProgress = false; // prevents overlapping checks/calls

async function mwSyncScrunchieGift() {
  if (mwGiftSyncInProgress) return;
  mwGiftSyncInProgress = true;

  try {
    const res = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
    const cart = await res.json();

    const hasBundle = cart.items.some(
      (item) => String(item.product_id) === MW_BUNDLE_PRODUCT_ID
    );
    const giftLine = cart.items.find(
      (item) => String(item.variant_id) === MW_SCRUNCHIE_VARIANT_ID
    );

    if (hasBundle && !giftLine) {
      await cartPost(ADD_URL, {
        items: [
          {
            id: Number(MW_SCRUNCHIE_VARIANT_ID),
            quantity: 1,
            properties: { _mw_gift: 'scrunchie_pack' }, // tags it as an auto-added gift
          },
        ],
      });
      refreshCart();
    } else if (!hasBundle && giftLine) {
      await cartPost(CHANGE_URL, { id: giftLine.key, quantity: 0 });
      refreshCart();
    }
  } catch (err) {
    console.error('[mw-cart-extras] scrunchie gift sync failed:', err);
  } finally {
    mwGiftSyncInProgress = false;
  }
}

// Run on initial load — covers direct product-page adds, page refreshes,
// and anyone landing in cart with the bundle already present.
document.addEventListener('DOMContentLoaded', mwSyncScrunchieGift);

// Safety net for adds/removes that happen outside this file (e.g. a
// product page "Add to cart" for the bundle itself). Horizon dispatches
// this after its own cart mutations.
document.addEventListener('cart:update', mwSyncScrunchieGift);